import {
  ErrorBoundaryKey,
  EventBusKey,
  GameLoopKey,
  InspectorKey,
  installInspector,
  SceneManagerKey,
  SceneHookRegistryKey,
  SceneRandomSourceKey,
} from "@yagejs/core";
import type {
  EngineContext,
  EventBus,
  EngineEvents,
  Plugin,
  SceneManager,
  System,
  SystemScheduler,
} from "@yagejs/core";
import { DebugRegistryKey } from "./types.js";
import { CameraComponent, RendererKey } from "@yagejs/renderer";
import type { RendererPlugin, SceneRenderTreeProvider } from "@yagejs/renderer";
import { SceneRenderTreeProviderKey } from "@yagejs/renderer";
import type { Application, Container } from "pixi.js";
import { DebugClock } from "./DebugClock.js";
import type { DebugClockHost } from "./DebugClock.js";
import { DebugRegistryImpl } from "./DebugRegistryImpl.js";
import { DebugScene } from "./DebugScene.js";
import { StatsStore } from "./StatsStore.js";
import { GraphicsPool } from "./GraphicsPool.js";
import { TextPool } from "./TextPool.js";
import { WorldDebugApiImpl } from "./WorldDebugApiImpl.js";
import { HudDebugApiImpl } from "./HudDebugApiImpl.js";
import { DebugRenderSystem } from "./DebugRenderSystem.js";
import { FpsContributor } from "./contributors/FpsContributor.js";
import { EntityCountContributor } from "./contributors/EntityCountContributor.js";
import { SystemTimingContributor } from "./contributors/SystemTimingContributor.js";
import { VectorContributor } from "./contributors/VectorContributor.js";

/** Configuration for the DebugPlugin. */
export interface DebugConfig {
  /** Key code to toggle debug overlay. Default: "Backquote" */
  toggleKey?: string;
  /** Key code to advance one fixed-timestep frame while the debug clock is frozen. Default: "Period" */
  stepKey?: string;
  /** Max pooled Graphics objects. Default: 256 */
  maxGraphics?: number;
  /** Max HUD text lines. Default: 32 */
  maxHudLines?: number;
  /** Whether the overlay starts enabled. Default: false */
  startEnabled?: boolean;
  /**
   * Whether the Inspector records bus, entity and scene events at startup.
   * Default: true. Set false to skip the per-event allocation entirely (the EventBus
   * tap never attaches) when nothing reads `inspector.events.getLog()` or
   * `waitFor()` — toggle back on at runtime with `inspector.events.setEnabled(true)`.
   */
  eventLog?: boolean;
  /** Initial flag overrides, keyed by "contributorName.flagName". */
  flags?: Record<string, boolean>;
  /**
   * If set, every scene's RNG is initialized with this seed instead of an
   * unspecified default. Use for deterministic E2E runs. Leave undefined for
   * normal debug builds so randomness behaves the same as in production.
   */
  deterministicSeed?: number;
  /**
   * If true, the engine boots with a paused Pixi ticker so no auto-frames
   * tick before user code can intervene — equivalent to calling
   * `inspector.time.freeze()` at frame zero, but applied during plugin
   * `install()` (before `loop.start()`). Use for deterministic E2E runs:
   * any later `engine.scenes.push(...)` resolves under frozen time, so
   * snapshots taken right after only depend on the explicit `time.step()`
   * calls — not on how many auto-frames slipped in between
   * `await engine.start()` and the test's first `freeze()`.
   */
  startFrozen?: boolean;
}

export interface LayerTransformSnapshot {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
}

export interface CameraStackSnapshot {
  scene: string;
  name: string | undefined;
  priority: number;
  /** Whether the camera applies: its own `enabled` flag and an active entity. */
  enabled: boolean;
}

/**
 * Renderer-aware diagnostics exposed through the inspector extension
 * namespace `debug`. Kept out of the core Inspector surface so the core
 * package stays renderer-agnostic while plugins can still publish optional
 * runtime helpers in a uniform way.
 */
export interface DebugDiagnostics {
  getLayerTransform(
    sceneName: string,
    layerName: string,
  ): LayerTransformSnapshot | undefined;
  getCameraStack(): CameraStackSnapshot[];
  /** Whether the HUD layer (text readouts) is currently visible. */
  isHudVisible(): boolean;
  /**
   * Show/hide the HUD layer — the screen-space text readouts (FPS, system
   * timings, entity counts). World-space debug graphics (collider outlines
   * etc.) are unaffected. The stage re-renders immediately, so the change
   * lands on the canvas even while the debug clock is frozen — capture
   * tooling can hide the wall-clock-dependent text before screenshotting
   * without stepping the simulation.
   */
  setHudVisible(visible: boolean): void;
}

/**
 * Debug overlay plugin. Mounts a private `DebugScene` through
 * `SceneManager._mountDetached` so it goes through the same scoped-DI
 * lifecycle as stacked scenes (the renderer's `beforeEnter` hook creates
 * its render tree) while staying off the user-visible scene stack.
 */
export class DebugPlugin implements Plugin {
  readonly name = "debug";
  readonly version = "3.0.0";
  readonly dependencies = ["renderer"] as const;

  private readonly config: DebugConfig;
  private registry!: DebugRegistryImpl;
  private stats!: StatsStore;
  private graphicsPool: GraphicsPool | null = null;
  private textPool: TextPool | null = null;
  private worldApi: WorldDebugApiImpl | null = null;
  private hudApi: HudDebugApiImpl | null = null;
  private renderSystem: DebugRenderSystem | null = null;
  private systemTimings = new Map<string, number>();
  private originalUpdates = new Map<System, (dt: number) => void>();
  private keyListener: ((e: KeyboardEvent) => void) | null = null;
  private context!: EngineContext;
  private renderer!: RendererPlugin;
  private scheduler!: SystemScheduler;
  private sceneManager!: SceneManager;
  private debugScene: DebugScene | null = null;
  private hudContainer: Container | null = null;
  private provider: SceneRenderTreeProvider | null = null;
  private eventUnsubs: Array<() => void> = [];
  private clock: DebugClock | null = null;
  private uninstallInspector: (() => void) | undefined;

  constructor(config?: DebugConfig) {
    const seed = config?.deterministicSeed;
    if (seed !== undefined && !Number.isFinite(seed)) {
      throw new Error(
        `DebugPlugin: deterministicSeed must be finite, got ${seed}.`,
      );
    }
    this.config = config ?? {};
  }

  install(context: EngineContext): void {
    this.context = context;
    // The overlay's clock, step key and event log all run through the
    // Inspector, so the plugin installs one when the game has not. An
    // Inspector the game installed itself is reused and left to its owner.
    this.uninstallInspector = installInspector(context);
    this.renderer = context.resolve(RendererKey);
    if (this.config.deterministicSeed !== undefined) {
      context
        .resolve(SceneRandomSourceKey)
        .setDefaultSeed(this.config.deterministicSeed);
    }
    if (this.config.startFrozen) {
      // Stop Pixi's ticker before `loop.start()` runs, so no frames tick
      // between `app.init()` (which auto-starts the ticker) and the user's
      // first `inspector.time.freeze()`. Without this, even the few auto-
      // frames that slip in between RendererPlugin install and user-space
      // `await engine.start()` resolution mutate physics / input clocks,
      // breaking bit-identical replay snapshots. The DebugClock created in
      // `onStart` will pick this up via its own `freeze()` call.
      this.renderer.application.stop();
    }

    this.registry = new DebugRegistryImpl();
    this.registry.enabled = this.config.startEnabled ?? false;

    if (this.config.flags) {
      for (const [key, value] of Object.entries(this.config.flags)) {
        const dot = key.indexOf(".");
        if (dot > 0) {
          this.registry.setFlag(key.slice(0, dot), key.slice(dot + 1), value);
        }
      }
    }

    this.stats = new StatsStore();
    this.eventUnsubs.push(
      context.resolve(SceneHookRegistryKey).register({
        afterExit: (scene) => this.worldApi?.releaseScene(scene),
      }),
    );

    // Drop a destroyed entity's `drawVector` registrations here rather than
    // while drawing: the overlay may never draw a frame, and a provider
    // closure holding a dead entity would keep it alive for the session.
    const bus = context.resolve(EventBusKey) as EventBus<EngineEvents>;
    this.eventUnsubs.push(
      bus.on("entity:destroyed", ({ entity }) => {
        this.registry.vectors.dropEntity(entity.id);
      }),
    );

    context.register(DebugRegistryKey, this.registry);
  }

  registerSystems(scheduler: SystemScheduler): void {
    // DebugRenderSystem is registered lazily once the debug scene is ready —
    // its constructor needs the scene's layer containers.
    this.scheduler = scheduler;
  }

  async onStart(): Promise<void> {
    this.sceneManager = this.context.resolve(SceneManagerKey);
    const bus = this.context.resolve(EventBusKey) as EventBus<EngineEvents>;

    // Key listeners (toggle, manual-clock step)
    const toggleKey = this.config.toggleKey ?? "Backquote";
    const stepKey = this.config.stepKey ?? "Period";
    this.keyListener = (e: KeyboardEvent) => {
      if (e.code === toggleKey) {
        this.registry.toggle();
        return;
      }
      if (e.code === stepKey && this.clock?.isFrozen) {
        e.preventDefault();
        this.context.resolve(InspectorKey).time.step();
      }
    };
    if (typeof window !== "undefined") {
      window.addEventListener("keydown", this.keyListener);
    }

    // Instrument system timings — reuse the scheduler captured in
    // `registerSystems`, which ran before `onStart`.
    for (const system of this.scheduler.getAllSystems()) {
      if (system instanceof DebugRenderSystem) continue;
      const name = system.constructor.name;
      const original = system.update.bind(system);
      this.originalUpdates.set(system, original);
      system.update = (dt: number) => {
        const t0 = performance.now();
        original(dt);
        this.systemTimings.set(name, performance.now() - t0);
      };
    }

    // Built-in contributors
    const inspector = this.context.resolve(InspectorKey);
    this.registry.register(new FpsContributor());
    this.registry.register(new EntityCountContributor(inspector));
    this.registry.register(new SystemTimingContributor(this.systemTimings));
    this.registry.register(
      new VectorContributor(
        this.registry.vectors,
        this.context.resolve(ErrorBoundaryKey),
      ),
    );

    // Manual clock for deterministic stepping. The host drives `app.ticker`
    // directly so a manual `step()` fires every ticker subscriber
    // (AnimatedSprite, pixi-filters, render, user `app.ticker.add` callbacks)
    // with the same synthetic dt as gameplay state.
    const gameLoop = this.context.resolve(GameLoopKey);
    const app = this.renderer.application;
    // The clock drives `app.ticker` in milliseconds (Pixi's `deltaMS` unit),
    // while `gameLoop.fixedTimestep` is in seconds — convert for the default
    // per-step delta.
    this.clock = new DebugClock(
      createPixiTickerHost(app, gameLoop.fixedTimestep * 1000, this.renderer),
    );
    inspector.attachTimeController(this.clock);
    inspector.setEventLogEnabled(this.config.eventLog ?? true);
    if (this.config.startFrozen) {
      // `install()` already called `app.stop()`. Run the clock's own
      // `freeze()` now to take ownership: it captures `minFPS`, zeroes
      // `syntheticTime` / `lastTime`, and flips `_isFrozen` so the public
      // `inspector.time.isFrozen()` reflects reality without a redundant
      // freeze cycle from the caller.
      this.clock.freeze();
    }

    // Materialize the debug scene off-stack. `_mountDetached` routes through
    // the same beforeEnter hooks as `push`, so the renderer materializes the
    // debug scene's tree and registers SceneRenderTreeKey on its scope.
    this.provider = this.context.tryResolve(SceneRenderTreeProviderKey) ?? null;
    this.registerInspectorDiagnostics();
    await this.materializeDebugScene();

    // Keep the debug scene visually on top of the user stack after any
    // push/pop/replace by reordering its root containers.
    const bringToFront = () => {
      if (this.debugScene && this.provider?.bringSceneToFront) {
        this.provider.bringSceneToFront(this.debugScene);
      }
    };
    this.eventUnsubs.push(bus.on("scene:pushed", bringToFront));
    this.eventUnsubs.push(bus.on("scene:popped", bringToFront));
    this.eventUnsubs.push(bus.on("scene:replaced", bringToFront));
  }

  onDestroy(): void {
    for (const unsub of this.eventUnsubs) unsub();
    this.eventUnsubs.length = 0;

    for (const [system, original] of this.originalUpdates) {
      system.update = original;
    }
    this.originalUpdates.clear();

    if (this.keyListener) {
      if (typeof window !== "undefined") {
        window.removeEventListener("keydown", this.keyListener);
      }
      this.keyListener = null;
    }

    // A tool that installed the Inspector itself may have removed it before
    // the engine is destroyed; there is nothing of ours to detach then.
    const inspector = this.context.tryResolve(InspectorKey);
    if (inspector) {
      inspector.removeExtension("debug");
      // Only detach our own clock — passing undefined would clear whatever
      // controller is registered, which could belong to another plugin if
      // onDestroy runs after a failed onStart.
      if (this.clock) {
        inspector.detachTimeController(this.clock);
      }
      inspector.setEventLogEnabled(false);
    }
    if (this.config.deterministicSeed !== undefined) {
      this.context.resolve(SceneRandomSourceKey).setDefaultSeed(undefined);
    }
    this.clock = null;

    for (const contributor of this.registry.contributors.values()) {
      contributor.dispose?.();
    }
    this.registry.contributors.clear();
    this.registry.vectors.clear();

    this.tearDownDebugInfra();
    this.teardownDebugScene();

    this.uninstallInspector?.();
    this.uninstallInspector = undefined;
  }

  private async materializeDebugScene(): Promise<void> {
    const scene = new DebugScene();
    scene.onReady = (worldContainer, hudContainer) =>
      this.setUpDebugInfra(worldContainer, hudContainer);
    scene.onTearDown = () => this.tearDownDebugInfra();
    await this.sceneManager._mountDetached(scene);
    this.debugScene = scene;
  }

  private teardownDebugScene(): void {
    // If destroy is called while `_mountDetached` is still pending, we don't
    // yet hold a reference to the partially-mounted scene. Skipping unmount
    // here is safe: onDestroy still runs `tearDownDebugInfra` below, and the
    // engine teardown tears down the renderer next which destroys any
    // half-created provider entries.
    if (!this.debugScene) return;
    this.sceneManager._unmountDetached(this.debugScene);
    this.debugScene = null;
    this.provider = null;
  }

  private setUpDebugInfra(
    worldContainer: Container,
    hudContainer: Container,
  ): void {
    const vw = this.renderer.virtualSize.width;
    const vh = this.renderer.virtualSize.height;

    this.graphicsPool = new GraphicsPool(
      worldContainer,
      this.config.maxGraphics,
    );
    this.textPool = new TextPool(hudContainer, this.config.maxHudLines);
    this.hudContainer = hudContainer;

    this.worldApi = new WorldDebugApiImpl(
      this.graphicsPool,
      this.registry,
      this.sceneManager,
      this.provider!,
      worldContainer,
    );
    this.hudApi = new HudDebugApiImpl(this.textPool, this.registry, vw, vh);

    this.renderSystem = new DebugRenderSystem(
      this.registry,
      this.graphicsPool,
      this.textPool,
      this.worldApi,
      this.hudApi,
      this.stats,
      worldContainer,
      hudContainer,
    );
    this.scheduler.add(this.renderSystem);
  }

  private tearDownDebugInfra(): void {
    if (this.renderSystem) {
      this.scheduler.remove(this.renderSystem);
      this.renderSystem = null;
    }
    this.graphicsPool?.destroy();
    this.textPool?.destroy();
    this.graphicsPool = null;
    this.textPool = null;
    this.worldApi = null;
    this.hudApi = null;
    this.hudContainer = null;
  }

  private registerInspectorDiagnostics(): void {
    const diagnostics: DebugDiagnostics = {
      getLayerTransform: (sceneName, layerName) => {
        const scene = this.sceneManager.all.find(
          (candidate) => candidate.name === sceneName,
        );
        if (!scene) return undefined;

        const layer = this.provider?.getTree(scene)?.tryGet(layerName);
        if (!layer) return undefined;

        const container = layer.container;
        return {
          x: container.position.x,
          y: container.position.y,
          scaleX: container.scale.x,
          scaleY: container.scale.y,
          rotation: container.rotation,
        };
      },
      getCameraStack: () => {
        const cameras: CameraStackSnapshot[] = [];
        for (const scene of this.sceneManager.all) {
          for (const entity of scene.getEntities()) {
            const cam = entity.tryGet(CameraComponent);
            if (!cam) continue;
            cameras.push({
              scene: scene.name,
              name: cam.cameraName,
              priority: cam.priority,
              enabled: cam.effectiveEnabled,
            });
          }
        }
        return cameras;
      },
      isHudVisible: () => this.hudContainer?.visible ?? false,
      setHudVisible: (visible) => {
        if (!this.hudContainer || this.hudContainer.visible === visible) {
          return;
        }
        this.hudContainer.visible = visible;
        // Render synchronously so the toggle reaches the canvas even while
        // the debug clock is frozen (no ticker frames are running).
        this.renderer.application.render();
      },
    };
    this.context.resolve(InspectorKey).addExtension("debug", diagnostics);
  }
}

/**
 * Wire a `DebugClockHost` against a Pixi v8 `Application`. Driving
 * `app.ticker.update(syntheticTime)` while the ticker is stopped fires every
 * subscriber — Pixi's `Ticker.update` doesn't gate on `started`, only the
 * rAF-driven `_tick` does — so a single advance replaces the old
 * `gameLoop.tick(dt)` + `app.render()` quad and along the way fixes the
 * AnimatedSprite / pixi-filters / `app.ticker.add` subscriber freeze.
 */
function createPixiTickerHost(
  app: Application,
  fixedTimestep: number,
  renderer: RendererPlugin,
): DebugClockHost {
  let syntheticTime = 0;
  let savedMinFPS: number | null = null;
  return {
    fixedTimestep,
    freeze(): void {
      app.stop();
      // Reset both to zero so synthetic time starts from a clean origin
      // and `(syntheticTime + dt) - syntheticTime` stays an exact `dt`
      // — keeps deltaMS deterministic regardless of how long the page
      // ran in auto mode before the freeze.
      syntheticTime = 0;
      app.ticker.lastTime = 0;
      // Default minFPS=10 clamps deltaMS at 100ms — `step(200)` would
      // silently see deltaMS=100. Disable for the duration of the freeze.
      savedMinFPS = app.ticker.minFPS;
      app.ticker.minFPS = 0;
    },
    thaw(): void {
      if (savedMinFPS !== null) {
        app.ticker.minFPS = savedMinFPS;
        savedMinFPS = null;
      }
      // `app.start()` calls `Ticker._requestIfNeeded` which resets
      // `lastTime` to `performance.now()` itself, so the next rAF
      // doesn't see `realNow - 0` as a giant first-frame delta. No
      // need to set it manually here.
      app.start();
    },
    renderFrame(): void {
      renderer.render();
    },
    get drawingEnabled(): boolean {
      return renderer.drawingEnabled;
    },
    set drawingEnabled(enabled: boolean) {
      renderer.drawingEnabled = enabled;
    },
    advance(dtMs: number): void {
      syntheticTime += dtMs;
      app.ticker.update(syntheticTime);
    },
  };
}
